// src/pages/Vendor/Apply/components/Step2Legal.tsx
import React, { useState } from 'react';
import { FileText, Upload, AlertCircle, Info } from 'lucide-react';

interface Step2LegalProps {
  data: any;
  onNext: (data: any) => void;
  onBack: () => void;
}

const Step2Legal: React.FC<Step2LegalProps> = ({ data, onNext, onBack }) => {
  const [form, setForm] = useState({
    business_type: data.business_type || '',
    registration_number: data.registration_number || '',
    tax_id: data.tax_id || '',
    registration_file: data.registration_file || null,
    address_proof: data.address_proof || null,
    termsAccepted: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isRegisteredBusiness = ['sole_proprietor', 'llc', 'corporation', 'partnership', 'non_profit'].includes(form.business_type);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Registration number - required for registered businesses only
    if (isRegisteredBusiness && !form.registration_number.trim()) {
      newErrors.registration_number = 'Registration number required';
    }

    // Tax ID - required for registered businesses only
    if (isRegisteredBusiness && !form.tax_id.trim()) {
      newErrors.tax_id = 'Tax ID required';
    }

    // Documents - required for everyone
    if (!form.registration_file) {
      newErrors.registration_file = isRegisteredBusiness
        ? 'Registration certificate required'
        : 'Identity document required';
    }
    if (!form.address_proof) {
      newErrors.address_proof = 'Address proof required';
    }
    // Check terms
    if (!form.termsAccepted) {
      newErrors.terms = 'You must accept the Terms of Service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext(form);
    }
  };

  // Get document label based on business type
  const getDocumentLabel = () => {
    return isRegisteredBusiness ? 'Registration Certificate' : 'Identity Document (Passport/ID)';
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Legal & Documentation</h2>
      <p className="text-sm text-gray-500 mb-6">Provide your business registration details</p>

      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            All documents remain confidential and are only used for verification purposes.
          </p>
        </div>

        {/* Conditional help text based on business type */}
        {form.business_type === 'individual' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              You'll sell under your legal name. No registration or tax ID required. Just upload your ID for verification.
            </p>
          </div>
        )}

        {form.business_type === 'sole_proprietor' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              You have a registered business but trade as an individual. Registration and tax ID required.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Registration Number {isRegisteredBusiness && '*'}
            </label>
            <input
              type="text"
              value={form.registration_number}
              onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
              placeholder={isRegisteredBusiness ? 'e.g., 123456789' : 'Not required for individuals'}
              disabled={!isRegisteredBusiness}
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.registration_number ? 'border-red-500' : 'border-gray-300'
                } ${!isRegisteredBusiness ? 'bg-gray-100 text-gray-500' : ''}`}
            />
            {errors.registration_number && (
              <p className="text-sm text-red-500 mt-1">{errors.registration_number}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tax ID / VAT Number {isRegisteredBusiness && '*'}
            </label>
            <input
              type="text"
              value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              placeholder={isRegisteredBusiness ? 'e.g., 123-456-789' : 'Not required for individuals'}
              disabled={!isRegisteredBusiness}
              className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.tax_id ? 'border-red-500' : 'border-gray-300'
                } ${!isRegisteredBusiness ? 'bg-gray-100 text-gray-500' : ''}`}
            />
            {errors.tax_id && (
              <p className="text-sm text-red-500 mt-1">{errors.tax_id}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getDocumentLabel()} *
            </label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer ${errors.registration_file ? 'border-red-500' : 'border-gray-300'
              }`}>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.png"
                onChange={(e) => setForm({ ...form, registration_file: e.target.files?.[0] || null })}
                id="registration-file"
              />
              <label htmlFor="registration-file" className="cursor-pointer">
                {form.registration_file ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <FileText className="h-5 w-5" />
                    <span>{form.registration_file.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-1">Upload {getDocumentLabel().toLowerCase()}</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG (max 5MB)</p>
                  </>
                )}
              </label>
            </div>
            {errors.registration_file && (
              <p className="text-sm text-red-500 mt-1">{errors.registration_file}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof of Address *
            </label>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:bg-gray-50 transition-colors cursor-pointer ${errors.address_proof ? 'border-red-500' : 'border-gray-300'
              }`}>
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.png"
                onChange={(e) => setForm({ ...form, address_proof: e.target.files?.[0] || null })}
                id="address-file"
              />
              <label htmlFor="address-file" className="cursor-pointer">
                {form.address_proof ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <FileText className="h-5 w-5" />
                    <span>{form.address_proof.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-1">Upload utility bill or bank statement</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG (max 5MB)</p>
                  </>
                )}
              </label>
            </div>
            {errors.address_proof && (
              <p className="text-sm text-red-500 mt-1">{errors.address_proof}</p>
            )}
          </div>
        </div>

        <div className={`bg-gray-50 rounded-lg p-4 border ${errors.terms ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.termsAccepted}
              onChange={(e) => {
                setForm({ ...form, termsAccepted: e.target.checked });
                if (errors.terms) {
                  const newErrors = { ...errors };
                  delete newErrors.terms;
                  setErrors(newErrors);
                }
              }}
              className={`mt-1 flex-shrink-0 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${errors.terms ? 'border-red-500' : ''
                }`}
            />
            <div>
              <p className="text-sm font-medium text-gray-700">
                Terms of Service <span className="text-red-500">*</span>
              </p>
              <p className="text-xs text-gray-500">
                I agree to the <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Vendor Terms of Service</a> and confirm that all information provided is accurate.
              </p>
              {errors.terms && (
                <div className="mt-1 flex items-center text-sm text-red-600">
                  <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  {errors.terms}
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors"
        >
          ← Back
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
        >
          Review Application →
        </button>
      </div>
    </form>
  );
};

export default Step2Legal;